import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link } from 'react-router-dom';
import {
  ArrowRight, BadgeCheck, Bot, Building2, ChevronDown, ChevronUp,
  Cpu, FileText, Globe, Mail, MessageSquare, Package,
  ShieldCheck, ShoppingCart, Sparkles, Target, TrendingUp,
  Users, Wrench, Zap,
} from 'lucide-react';
import SEO from '../components/SEO';
import RelatedPages from '../components/RelatedPages';

const fade   = { hidden: { opacity: 0, y: 20 }, show: { opacity: 1, y: 0 } };
const stagger = { hidden: {}, show: { transition: { staggerChildren: 0.08 } } };

/* ── Datos por sector ──────────────────────────────────────────── */
const SECTORS = [
  {
    id: 'ecommerce',
    Icon: ShoppingCart,
    label: 'E-commerce',
    accent: '#00E5FF',
    accentBg: 'bg-cyan-400/10',
    accentBorder: 'border-cyan-400/30',
    accentText: 'text-cyan-300',
    ringColor: 'ring-cyan-400/30',
    pain: 'Gestión manual de pedidos, devoluciones y atención al cliente que escala mal con el volumen.',
    painDetail: [
      'El equipo responde las mismas preguntas decenas de veces al día',
      'Las devoluciones se procesan manualmente con riesgo de error',
      'El cobro y el seguimiento post-venta son procesos desconectados',
    ],
    solution: 'Agente Comercial + Agente de Cobro + Agente de Soporte',
    solutionDetail: [
      'El Agente Comercial clasifica y cualifica leads desde el primer contacto',
      'El Agente de Cobro genera facturas con Stripe y registra pagos automáticamente',
      'El Agente de Soporte responde preguntas frecuentes y escala las excepciones al equipo',
    ],
    tools: ['Stripe', 'Gmail', 'Notion'],
    toolIcons: [Zap, Mail, FileText],
    metric: 'Diseñado para reducir el tiempo de respuesta al cliente y el trabajo manual de gestión de pedidos.',
    cta: 'Ver ruta de activación para E-commerce',
    ctaHref: '/activar',
  },
  {
    id: 'servicios-profesionales',
    Icon: Wrench,
    label: 'Servicios Profesionales',
    accent: '#10B981',
    accentBg: 'bg-emerald-400/10',
    accentBorder: 'border-emerald-400/30',
    accentText: 'text-emerald-300',
    ringColor: 'ring-emerald-400/30',
    pain: 'Onboarding de clientes lento y contratos que se firman, envían y siguen manualmente.',
    painDetail: [
      'El proceso de onboarding implica intercambios de correo y documentos sin estructura',
      'Los contratos se generan a mano y el seguimiento depende de la memoria del equipo',
      'El cobro inicial tarda más de lo necesario por falta de automatización',
    ],
    solution: 'Agente de Onboarding + Agente de Cobro',
    solutionDetail: [
      'El Agente de Onboarding guía al cliente desde el primer contacto hasta la firma del contrato',
      'Los contratos se generan, envían y registran automáticamente con cada cliente nuevo',
      'El Agente de Cobro emite la factura inicial y configura los pagos recurrentes sin intervención manual',
    ],
    tools: ['Stripe', 'Google Drive', 'Calendar'],
    toolIcons: [Zap, FileText, Globe],
    metric: 'Objetivo: reducir el tiempo desde el primer contacto hasta el primer cobro, sin aumentar la carga del equipo.',
    cta: 'Ver ruta de activación para Servicios Profesionales',
    ctaHref: '/activar',
  },
  {
    id: 'administracion-publica',
    Icon: Building2,
    label: 'Administración Pública / ONG',
    accent: '#8B5CF6',
    accentBg: 'bg-violet-400/10',
    accentBorder: 'border-violet-400/30',
    accentText: 'text-violet-300',
    ringColor: 'ring-violet-400/30',
    pain: 'Gestión de incidencias y coordinación entre departamentos sin trazabilidad ni registro centralizado.',
    painDetail: [
      'Las incidencias se registran en múltiples canales sin consolidación',
      'La coordinación entre departamentos depende de correos y reuniones no documentadas',
      'No existe registro auditable de quién decidió qué y cuándo',
    ],
    solution: 'Agente de Operaciones + Agente de Seguridad',
    solutionDetail: [
      'El Agente de Operaciones centraliza y categoriza incidencias con registro completo de cada acción',
      'El Agente de Seguridad audita accesos, permisos y decisiones con log inmutable',
      'Cada escalado requiere aprobación humana explícita, con trazabilidad completa',
    ],
    tools: ['Notion', 'Slack', 'Sheets'],
    toolIcons: [FileText, MessageSquare, Globe],
    metric: 'Diseñado para reducir el tiempo de respuesta sin comprometer el control ni la trazabilidad.',
    cta: 'Ver ruta de activación para Administración Pública',
    ctaHref: '/activar',
  },
  {
    id: 'agencias-partners',
    Icon: Users,
    label: 'Agencias y Partners',
    accent: '#F59E0B',
    accentBg: 'bg-amber-400/10',
    accentBorder: 'border-amber-400/30',
    accentText: 'text-amber-300',
    ringColor: 'ring-amber-400/30',
    pain: 'Gestión de múltiples clientes con herramientas desconectadas y sin visibilidad unificada.',
    painDetail: [
      'Cada cliente vive en un stack diferente sin panel de control unificado',
      'El reporte de resultados es manual y consume tiempo del equipo cada semana',
      'Las automatizaciones se construyen una por una sin reutilización entre clientes',
    ],
    solution: 'Todos los agentes con paneles independientes por cliente',
    solutionDetail: [
      'Cada cliente tiene su propio workspace con agentes, datos y permisos aislados',
      'Panel partner con métricas consolidadas de todos los clientes en una vista',
      'Onboarding de un nuevo cliente en menos de 10 minutos reutilizando flujos configurados',
    ],
    tools: ['HubSpot', 'Gmail', 'Slack', 'Drive'],
    toolIcons: [Target, Mail, MessageSquare, FileText],
    metric: 'Objetivo: reducir el tiempo de onboarding por cliente y el trabajo de reporte semanal.',
    cta: 'Ver ruta de activación para Agencias',
    ctaHref: '/unirse',
  },
];

/* ── Datos por rol ─────────────────────────────────────────────── */
const ROLES = [
  {
    id: 'direccion',
    Icon: Sparkles,
    label: 'Dirección',
    accent: '#00E5FF',
    accentText: 'text-cyan-300',
    accentBorder: 'border-cyan-400/25',
    accentBg: 'bg-cyan-400/6',
    pain: 'Toma decisiones con información dispersa, sin una vista operativa consolidada en tiempo real.',
    solution: 'Panel ejecutivo con KPIs de todos los agentes: leads, facturas, incidencias y agenda en una vista.',
    tools: 'Dashboard unificado · Alertas automáticas · Log de decisiones del equipo',
    metric: 'Objetivo: reducir el tiempo dedicado a reuniones de alineación interna.',
    cta: 'Ver demo del panel de dirección',
    ctaHref: '/experiencia-3d',
  },
  {
    id: 'operaciones',
    Icon: Wrench,
    label: 'Operaciones',
    accent: '#10B981',
    accentText: 'text-emerald-300',
    accentBorder: 'border-emerald-400/25',
    accentBg: 'bg-emerald-400/6',
    pain: 'Coordina procesos manuales entre herramientas desconectadas, con mucha fricción y pocas excepciones automatizadas.',
    solution: 'Agentes que ejecutan las operaciones repetitivas y escalan las excepciones con contexto completo.',
    tools: 'Notion · Slack · Sheets · Calendar',
    metric: 'Diseñado para reducir el tiempo de coordinación manual entre departamentos.',
    cta: 'Ver cómo funciona en operaciones',
    ctaHref: '/sistema-multiagente',
  },
  {
    id: 'ventas',
    Icon: TrendingUp,
    label: 'Ventas',
    accent: '#F59E0B',
    accentText: 'text-amber-300',
    accentBorder: 'border-amber-400/25',
    accentBg: 'bg-amber-400/6',
    pain: 'El pipeline se gestiona manualmente: clasificación de leads, seguimiento y facturación consumen tiempo del equipo comercial.',
    solution: 'Agente Comercial que clasifica, puntúa y mueve leads en el pipeline; escala a aprobación humana en los pasos clave.',
    tools: 'CRM integrado · WhatsApp Business · Gmail · Stripe',
    metric: 'Objetivo: reducir el tiempo entre el primer contacto y la primera propuesta.',
    cta: 'Ver el agente de ventas',
    ctaHref: '/sistema-multiagente',
  },
  {
    id: 'marketing',
    Icon: Target,
    label: 'Marketing',
    accent: '#EC4899',
    accentText: 'text-pink-300',
    accentBorder: 'border-pink-400/25',
    accentBg: 'bg-pink-400/6',
    pain: 'La coordinación de campañas, canales y mensajes depende de trabajo manual y herramientas sin integración real.',
    solution: 'Agentes que coordinan campañas, programan contenidos y reportan resultados con datos reales del pipeline.',
    tools: 'Redes sociales · Email · WhatsApp · Analytics',
    metric: 'Diseñado para reducir el tiempo de coordinación entre canales y automatizar el seguimiento de campañas.',
    cta: 'Ver integraciones disponibles',
    ctaHref: '/integraciones/deepseek',
  },
  {
    id: 'it-seguridad',
    Icon: ShieldCheck,
    label: 'IT / Seguridad',
    accent: '#EF4444',
    accentText: 'text-red-300',
    accentBorder: 'border-red-400/25',
    accentBg: 'bg-red-400/6',
    pain: 'Desplegar agentes de IA implica nuevas superficies de ataque: inyección de prompt, permisos excesivos, falta de trazabilidad.',
    solution: 'Arquitectura de permisos mínimos por agente, Auth Links revocables, log inmutable y Human-in-the-loop en acciones sensibles.',
    tools: 'Auth Links · Control de permisos · Log de auditoría · Revocación instantánea',
    metric: 'Objetivo: desplegar agentes sin ampliar la superficie de ataque ni sacrificar el control operativo.',
    cta: 'Ver los 6 pilares de seguridad',
    ctaHref: '/seguridad-agentes',
  },
];

/* ── Componentes internos ──────────────────────────────────────── */
function ToolChip({ label, Icon }) {
  return (
    <span className="inline-flex items-center gap-1.5 text-[10px] font-semibold text-slate-400 border border-white/10 bg-white/[0.04] rounded-full px-2.5 py-1">
      <Icon className="w-3 h-3" /> {label}
    </span>
  );
}

function SectorCard({ sector, active, onToggle }) {
  const { id, Icon, label, accent, accentBg, accentBorder, accentText, ringColor,
          pain, painDetail, solution, solutionDetail, tools, toolIcons, metric, cta, ctaHref } = sector;
  return (
    <motion.div
      variants={fade}
      className={`rounded-2xl border transition-all duration-300 overflow-hidden ${accentBorder} ${accentBg}
        ${active ? `ring-1 ${ringColor}` : 'hover:border-white/16'}`}
      data-testid={`sector-card-${id}`}
    >
      {/* Header — always visible */}
      <button
        onClick={onToggle}
        className="w-full flex items-center justify-between gap-4 p-6 text-left"
        data-testid={`sector-toggle-${id}`}
      >
        <div className="flex items-center gap-4">
          <div className={`w-11 h-11 rounded-xl ${accentBg} border ${accentBorder} flex items-center justify-center flex-shrink-0`}>
            <Icon className={`w-5 h-5 ${accentText}`} />
          </div>
          <div>
            <p className={`text-[10px] font-black uppercase tracking-widest mb-0.5 ${accentText}`}>{label}</p>
            <p className="text-white text-sm font-semibold leading-snug line-clamp-1">{pain}</p>
          </div>
        </div>
        {active
          ? <ChevronUp className="w-5 h-5 text-slate-400 flex-shrink-0" />
          : <ChevronDown className="w-5 h-5 text-slate-400 flex-shrink-0" />
        }
      </button>

      {/* Detail — expandable */}
      <AnimatePresence initial={false}>
        {active && (
          <motion.div
            key="detail"
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.32, ease: 'easeInOut' }}
            className="overflow-hidden"
          >
            <div className="px-6 pb-6 border-t border-white/6 pt-5 grid sm:grid-cols-2 gap-6">
              {/* Columna izquierda */}
              <div>
                <p className="text-[10px] font-bold uppercase tracking-widest text-slate-500 mb-3">Dolor identificado</p>
                <ul className="space-y-2 mb-5">
                  {painDetail.map(p => (
                    <li key={p} className="flex items-start gap-2 text-sm text-slate-300">
                      <span className="w-1 h-1 rounded-full bg-slate-500 flex-shrink-0 mt-2" />
                      {p}
                    </li>
                  ))}
                </ul>
                <p className="text-[10px] font-bold uppercase tracking-widest text-slate-500 mb-2">Herramientas conectadas</p>
                <div className="flex flex-wrap gap-2">
                  {tools.map((t, i) => <ToolChip key={t} label={t} Icon={toolIcons[i] || Zap} />)}
                </div>
              </div>

              {/* Columna derecha */}
              <div>
                <p className="text-[10px] font-bold uppercase tracking-widest text-slate-500 mb-2">Solución Neuralia</p>
                <p className={`text-sm font-bold mb-3 ${accentText}`}>{solution}</p>
                <ul className="space-y-2 mb-5">
                  {solutionDetail.map(s => (
                    <li key={s} className="flex items-start gap-2 text-sm text-slate-300">
                      <BadgeCheck className={`w-3.5 h-3.5 flex-shrink-0 mt-0.5 ${accentText}`} />
                      {s}
                    </li>
                  ))}
                </ul>
                <p className="text-[11px] italic text-slate-400 mb-5 border-l-2 pl-3" style={{ borderColor: `${accent}60` }}>
                  {metric}
                </p>
                <Link to={ctaHref}
                  className="inline-flex items-center gap-2 text-sm font-bold group transition-colors"
                  style={{ color: accent }}
                  data-testid={`sector-cta-${id}`}
                >
                  {cta} <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                </Link>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
}

function RoleCard({ role }) {
  const { id, Icon, label, accentText, accentBorder, accentBg, accent,
          pain, solution, tools, metric, cta, ctaHref } = role;
  return (
    <motion.div
      variants={fade}
      className={`rounded-2xl border ${accentBorder} ${accentBg} p-6 flex flex-col`}
      data-testid={`role-card-${id}`}
    >
      <div className={`w-10 h-10 rounded-xl ${accentBg} border ${accentBorder} flex items-center justify-center mb-4`}>
        <Icon className={`w-5 h-5 ${accentText}`} />
      </div>
      <p className={`text-[10px] font-black uppercase tracking-widest mb-3 ${accentText}`}>{label}</p>

      <div className="mb-3">
        <p className="text-[10px] font-bold uppercase tracking-widest text-slate-500 mb-1.5">Dolor</p>
        <p className="text-sm text-slate-300 leading-relaxed">{pain}</p>
      </div>

      <div className="mb-3">
        <p className="text-[10px] font-bold uppercase tracking-widest text-slate-500 mb-1.5">Solución</p>
        <p className="text-sm text-slate-300 leading-relaxed">{solution}</p>
      </div>

      <div className="mb-4">
        <p className="text-[10px] font-bold uppercase tracking-widest text-slate-500 mb-1.5">Herramientas</p>
        <p className="text-xs text-slate-400">{tools}</p>
      </div>

      <p className="text-[11px] italic text-slate-400 mb-5 border-l-2 pl-3 mt-auto" style={{ borderColor: `${accent}50` }}>
        {metric}
      </p>

      <Link to={ctaHref}
        className={`inline-flex items-center gap-2 text-xs font-bold group transition-colors ${accentText} hover:text-white`}
        data-testid={`role-cta-${id}`}
      >
        {cta} <ArrowRight className="w-3.5 h-3.5 group-hover:translate-x-1 transition-transform" />
      </Link>
    </motion.div>
  );
}

/* ── Página principal ──────────────────────────────────────────── */
export default function SolucionesPage() {
  const [activeSector, setActiveSector] = useState('ecommerce');

  const toggleSector = (id) => setActiveSector(prev => prev === id ? null : id);

  return (
    <>
      <SEO
        title="Soluciones por sector y por rol — E-commerce, Servicios, Administración Pública, Agencias | Neuralia 369"
        description="Neuralia 369 adaptado a tu operación: E-commerce, Servicios Profesionales, Administración Pública y Agencias. Dolor concreto, agentes específicos, herramientas conectadas."
        path="/soluciones"
        schemas={[
          {
            "@context": "https://schema.org",
            "@type": "Product",
            "name": "Neuralia 369 — Sistema Multiagente",
            "description": "Plataforma de agentes de IA para ventas, agenda, CRM y cobros. Sin código, sin infraestructura propia.",
            "brand": { "@type": "Brand", "name": "Neuralia369" },
            "offers": { "@type": "Offer", "priceCurrency": "USD", "price": "297", "priceValidUntil": "2027-12-31", "availability": "https://schema.org/InStock" }
          },
          {
            "@context": "https://schema.org",
            "@type": "FAQPage",
            "mainEntity": [
              { "@type": "Question", "name": "¿Cuánto tiempo tarda la activación?", "acceptedAnswer": { "@type": "Answer", "text": "El onboarding estándar es de 72 h hábiles. En el plan Enterprise puede extenderse según el número de integraciones." } },
              { "@type": "Question", "name": "¿Funciona para clínicas y consultorios?", "acceptedAnswer": { "@type": "Answer", "text": "Sí. Neuralia 369 tiene un módulo CRM clínico validado en clínicas dentales y consultorios médicos privados en Lima." } },
              { "@type": "Question", "name": "¿Se puede probar antes de contratar?", "acceptedAnswer": { "@type": "Answer", "text": "Ofrecemos un periodo de prueba de 14 días sin tarjeta de crédito en los planes Starter y Pro." } }
            ]
          }
        ]}
      />

      {/* ── HERO ── */}
      <section className="relative pt-28 pb-16 bg-[#02060f] border-b border-white/6 overflow-hidden" data-testid="soluciones-hero">
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute top-0 right-1/4 w-[500px] h-[400px] rounded-full bg-cyan-400/[0.03] blur-[100px]" />
          <div className="absolute bottom-0 left-1/4 w-[400px] h-[300px] rounded-full bg-violet-400/[0.03] blur-[80px]" />
        </div>
        <div className="relative max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div variants={stagger} initial="hidden" animate="show">
            <motion.p variants={fade} className="editorial-kicker mb-5 text-slate-400">
              Soluciones · Por sector y por rol
            </motion.p>
            <motion.h1 variants={fade} className="text-white font-heading text-4xl sm:text-5xl font-black leading-tight mb-5" data-testid="soluciones-title">
              El mismo sistema,{' '}
              <span className="gradient-text">adaptado a tu operación</span>
            </motion.h1>
            <motion.p variants={fade} className="text-slate-300 text-lg max-w-2xl leading-relaxed">
              Neuralia 369 no es una solución genérica. Cada sector tiene dolores específicos
              y cada rol tiene necesidades distintas. Aquí están los agentes, las herramientas
              y los flujos para cada uno.
            </motion.p>
          </motion.div>
        </div>
      </section>

      {/* ── POR SECTOR ── */}
      <section className="py-16 sm:py-20 bg-[#030a17] border-b border-white/6" data-testid="soluciones-sectors">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div variants={fade} initial="hidden" whileInView="show" viewport={{ once: true }} className="mb-10">
            <p className="editorial-kicker mb-3 text-slate-400">Por sector</p>
            <h2 className="text-white font-heading text-3xl sm:text-4xl font-black mb-3">
              Tu sector, tus agentes
            </h2>
            <p className="text-slate-400 text-base max-w-xl">
              Selecciona tu sector para ver el dolor identificado, los agentes recomendados,
              las herramientas conectadas y la ruta de activación.
            </p>
          </motion.div>

          <motion.div variants={stagger} initial="hidden" whileInView="show" viewport={{ once: true }} className="space-y-3">
            {SECTORS.map(sector => (
              <SectorCard
                key={sector.id}
                sector={sector}
                active={activeSector === sector.id}
                onToggle={() => toggleSector(sector.id)}
              />
            ))}
          </motion.div>
        </div>
      </section>

      {/* ── POR ROL ── */}
      <section className="py-16 sm:py-20 bg-[#02060f] border-b border-white/6" data-testid="soluciones-roles">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div variants={fade} initial="hidden" whileInView="show" viewport={{ once: true }} className="mb-10">
            <p className="editorial-kicker mb-3 text-slate-400">Por rol</p>
            <h2 className="text-white font-heading text-3xl sm:text-4xl font-black mb-3">
              Lo que Aynara hace por tu rol
            </h2>
            <p className="text-slate-400 text-base max-w-xl">
              Cada puesto tiene una relación diferente con los agentes. Aquí está
              el dolor específico, la solución concreta y las herramientas para cada rol.
            </p>
          </motion.div>

          <motion.div variants={stagger} initial="hidden" whileInView="show" viewport={{ once: true }}
            className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {ROLES.map(role => <RoleCard key={role.id} role={role} />)}
          </motion.div>
        </div>
      </section>

      {/* ── COMPARATIVA RÁPIDA ── */}
      <section className="py-14 bg-[#030b18] border-b border-white/6" data-testid="soluciones-quick-compare">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div variants={fade} initial="hidden" whileInView="show" viewport={{ once: true }} className="rounded-2xl border border-white/8 bg-white/[0.02] overflow-hidden">
            <div className="px-6 py-5 border-b border-white/6">
              <p className="text-xs font-bold uppercase tracking-widest text-slate-500">Resumen de agentes disponibles por sector</p>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-white/6">
                    <th className="px-5 py-3 text-left text-[11px] font-bold uppercase tracking-widest text-slate-500 w-44">Sector</th>
                    <th className="px-5 py-3 text-left text-[11px] font-bold uppercase tracking-widest text-slate-500">Agentes</th>
                    <th className="px-5 py-3 text-left text-[11px] font-bold uppercase tracking-widest text-slate-500">Herramientas clave</th>
                  </tr>
                </thead>
                <tbody>
                  {SECTORS.map((s, i) => (
                    <tr key={s.id} className={`border-b border-white/4 ${i % 2 === 0 ? '' : 'bg-white/[0.015]'}`} data-testid={`compare-row-${s.id}`}>
                      <td className="px-5 py-3.5">
                        <span className={`text-xs font-bold ${s.accentText}`}>{s.label}</span>
                      </td>
                      <td className="px-5 py-3.5 text-slate-300 text-xs">{s.solution}</td>
                      <td className="px-5 py-3.5">
                        <div className="flex flex-wrap gap-1.5">
                          {s.tools.map(t => (
                            <span key={t} className="text-[10px] text-slate-400 border border-white/10 rounded-full px-2 py-0.5">{t}</span>
                          ))}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </motion.div>
        </div>
      </section>

      {/* ── CTA FINAL ── */}
      <section className="py-20 bg-[#02060f]" data-testid="soluciones-final-cta">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div variants={stagger} initial="hidden" whileInView="show" viewport={{ once: true }}>
            <motion.div variants={fade} className="w-14 h-14 rounded-2xl bg-cyan-400/10 border border-cyan-400/25 flex items-center justify-center mx-auto mb-6">
              <Bot className="w-7 h-7 text-cyan-300" />
            </motion.div>
            <motion.p variants={fade} className="editorial-kicker mb-4 text-slate-400">Activar ahora</motion.p>
            <motion.h2 variants={fade} className="text-white font-heading text-3xl sm:text-4xl font-black mb-5">
              ¿No ves tu caso aquí?
            </motion.h2>
            <motion.p variants={fade} className="text-slate-300 text-base leading-relaxed mb-8 max-w-xl mx-auto">
              Si tu operación no encaja exactamente con los sectores o roles de esta página,
              cuéntanoslo. Evaluamos cada caso y proponemos la arquitectura de agentes más
              adecuada antes de que firmes nada.
            </motion.p>
            <motion.div variants={fade} className="flex flex-wrap justify-center gap-4">
              <Link to="/activar" className="btn-primary" data-testid="soluciones-cta-primary">
                Hablar con el equipo <ArrowRight className="w-4 h-4" />
              </Link>
              <Link to="/sistema-multiagente" className="btn-secondary border-white/15 text-slate-200 hover:bg-white/5" data-testid="soluciones-cta-secondary">
                Ver el sistema en detalle
              </Link>
            </motion.div>
          </motion.div>
        </div>
      </section>
      <RelatedPages pages={['sistema-multiagente','seguridad-agentes','recursos','integraciones/deepseek']} />
    </>
  );
}
